import React, { useState } from 'react';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { PASSWORD_RULES } from '../../../utils/passwordUtils';
import styles from './PasswordField.module.scss';

export interface PasswordFieldProps {
  /** Binds an external `<label htmlFor>` and the inline error; effectively required for full a11y. */
  id?: string;
  /** Current value (fully controlled). */
  value: string;
  /** Receives the new value on every keystroke. */
  onChange: (value: string) => void;
  /** Inline error message; also turns the box red and marks the input `aria-invalid`. */
  error?: string;
  /** Hint for password managers — `current-password` for the existing pwd, `new-password` for a new one. */
  autoComplete?: 'current-password' | 'new-password';
  /** Native form field name. */
  name?: string;
  /** Placeholder text. */
  placeholder?: string;
  /** Show the live requirements checklist (use on the *new* password field only). */
  showChecklist?: boolean;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  id, value, onChange, error, autoComplete, name, placeholder, showChecklist,
}) => {
  const [visible, setVisible] = useState(false);
  const errorId = id ? `${id}-error` : undefined;
  const rootClass = [styles.field, error ? styles.invalid : ''].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <div className={styles.inputRow}>
        <input
          id={id}
          name={name}
          type={visible ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          className={styles.toggle}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          aria-controls={id}
          onClick={() => setVisible((v) => !v)}
        >
          {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </button>
      </div>
      {showChecklist && value.length > 0 && (
        <ul className={styles.checklist}>
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(value);
            return (
              <li key={rule.key} className={met ? styles.met : styles.unmet}>
                {/* role+aria-label expose met / not-met state to screen readers, not just color. */}
                <span className={styles.bullet} role="img" aria-label={met ? 'met' : 'not met'}>
                  {met ? '✓' : '○'}
                </span>
                {rule.label}
              </li>
            );
          })}
        </ul>
      )}
      {error && (
        <small id={errorId} role="alert" className={styles.error}>
          <span aria-hidden="true">⚠</span> {error}
        </small>
      )}
    </div>
  );
};

export default PasswordField;
